import { useState, useEffect } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { useAuth, API } from "@/App";
import { useLanguage } from "@/contexts/LanguageContext";
import { 
  Users, Mail, Phone, Clock, MessageSquare, 
  CheckCircle, XCircle, AlertCircle, ChevronRight,
  UserPlus, Calendar
} from "lucide-react";
import { toast } from "sonner";

const DietitianLeads = () => {
  const { token } = useAuth();
  const { t } = useLanguage();
  const [leads, setLeads] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedLead, setSelectedLead] = useState(null);
  const [showUpdateModal, setShowUpdateModal] = useState(false);
  const [updateForm, setUpdateForm] = useState({
    status: "",
    notes: ""
  });
  const [updating, setUpdating] = useState(false);
  const [filterStatus, setFilterStatus] = useState("all");

  useEffect(() => {
    fetchLeads();
  }, [token]);

  const fetchLeads = async () => {
    setLoading(true);
    try {
      const response = await fetch(`${API}/directory/my-leads`, {
        headers: { Authorization: `Bearer ${token}` },
        
      });
      
      if (response.ok) {
        const data = await response.json();
        setLeads(data.leads || []);
      } else {
        toast.error("Failed to load leads");
      }
    } catch (error) {
      console.error("Fetch leads error:", error);
      toast.error("Failed to load leads");
    } finally {
      setLoading(false);
    }
  };

  const handleUpdateStatus = (lead) => {
    setSelectedLead(lead);
    setUpdateForm({
      status: lead.status,
      notes: lead.notes || ""
    });
    setShowUpdateModal(true);
  };

  const submitStatusUpdate = async () => {
    if (!selectedLead) return;
    
    setUpdating(true);
    try {
      const response = await fetch(
        `${API}/directory/leads/${selectedLead.lead_id}/status?status=${updateForm.status}${updateForm.notes ? `&notes=${encodeURIComponent(updateForm.notes)}` : ''}`,
        {
          method: "PUT",
          headers: { Authorization: `Bearer ${token}` },
          
        }
      );
      
      if (response.ok) {
        toast.success("Lead status updated");
        setShowUpdateModal(false);
        fetchLeads();
      } else {
        toast.error("Failed to update status");
      }
    } catch (error) {
      console.error("Update status error:", error);
      toast.error("Failed to update status");
    } finally {
      setUpdating(false);
    }
  };

  const getStatusBadge = (status) => {
    const badges = {
      new: { label: t('physician.new_status'), className: "bg-blue-100 text-blue-700 border-blue-200" },
      contacted: { label: t('physician.contacted_status'), className: "bg-yellow-100 text-yellow-700 border-yellow-200" },
      converted: { label: t('physician.converted_status'), className: "bg-green-100 text-green-700 border-green-200" },
      closed: { label: t('physician.closed_status'), className: "bg-gray-100 text-gray-700 border-gray-200" }
    };
    const badge = badges[status] || badges.new;
    return <Badge variant="outline" className={badge.className}>{badge.label}</Badge>;
  };

  const getDiabetesTypeLabel = (type) => {
    const labels = {
      type1: "Type 1",
      type2: "Type 2",
      prediabetes: "Prediabetes",
      monitoring: "Monitoring"
    };
    return labels[type] || type;
  };

  const formatDate = (dateStr) => {
    const date = new Date(dateStr);
    return date.toLocaleDateString('en-US', { 
      month: 'short', 
      day: 'numeric',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const filteredLeads = filterStatus === "all" 
    ? leads 
    : leads.filter(l => l.status === filterStatus);

  const newLeadsCount = leads.filter(l => l.status === "new").length;

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="animate-pulse flex flex-col items-center gap-4">
          <div className="w-12 h-12 rounded-full bg-primary/20" />
          <p className="text-muted-foreground">Loading leads...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header with stats */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h2 className="font-heading text-2xl font-bold text-secondary">{t('physician.patient_inquiries')}</h2>
          <p className="text-muted-foreground">
            {t('physician.leads_subtitle')}
          </p>
        </div>
        <div className="flex items-center gap-3">
          {newLeadsCount > 0 && (
            <Badge className="bg-blue-500 text-white px-3 py-1">
              {newLeadsCount} new
            </Badge>
          )}
          <Select value={filterStatus} onValueChange={setFilterStatus}>
            <SelectTrigger className="w-[140px] rounded-xl" data-testid="filter-leads-status">
              <SelectValue placeholder="Filter" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">{t('physician.all_leads')}</SelectItem>
              <SelectItem value="new">{t('physician.new_status')}</SelectItem>
              <SelectItem value="contacted">{t('physician.contacted_status')}</SelectItem>
              <SelectItem value="converted">{t('physician.converted_status')}</SelectItem>
              <SelectItem value="closed">{t('physician.closed_status')}</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>

      {/* Stats Cards - Clickable to filter */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-2 sm:gap-4">
        <Card 
          className={`bg-blue-50 border-blue-200 cursor-pointer transition-all hover:shadow-md ${filterStatus === 'new' ? 'ring-2 ring-blue-400' : ''}`}
          onClick={() => setFilterStatus(filterStatus === 'new' ? 'all' : 'new')}
          data-testid="stat-new-leads"
        >
          <CardContent className="p-4 flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-blue-100 flex items-center justify-center">
              <AlertCircle className="w-5 h-5 text-blue-600" />
            </div>
            <div>
              <p className="text-2xl font-bold text-blue-700">{leads.filter(l => l.status === "new").length}</p>
              <p className="text-xs text-blue-600">{t('physician.new_status')}</p>
            </div>
          </CardContent>
        </Card>
        <Card 
          className={`bg-yellow-50 border-yellow-200 cursor-pointer transition-all hover:shadow-md ${filterStatus === 'contacted' ? 'ring-2 ring-yellow-400' : ''}`}
          onClick={() => setFilterStatus(filterStatus === 'contacted' ? 'all' : 'contacted')}
          data-testid="stat-contacted-leads"
        >
          <CardContent className="p-4 flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-yellow-100 flex items-center justify-center">
              <Phone className="w-5 h-5 text-yellow-600" />
            </div>
            <div>
              <p className="text-2xl font-bold text-yellow-700">{leads.filter(l => l.status === "contacted").length}</p>
              <p className="text-xs text-yellow-600">{t('physician.contacted_status')}</p>
            </div>
          </CardContent>
        </Card>
        <Card 
          className={`bg-green-50 border-green-200 cursor-pointer transition-all hover:shadow-md ${filterStatus === 'converted' ? 'ring-2 ring-green-400' : ''}`}
          onClick={() => setFilterStatus(filterStatus === 'converted' ? 'all' : 'converted')}
          data-testid="stat-converted-leads"
        >
          <CardContent className="p-4 flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-green-100 flex items-center justify-center">
              <CheckCircle className="w-5 h-5 text-green-600" />
            </div>
            <div>
              <p className="text-2xl font-bold text-green-700">{leads.filter(l => l.status === "converted").length}</p>
              <p className="text-xs text-green-600">{t('physician.converted_status')}</p>
            </div>
          </CardContent>
        </Card>
        <Card 
          className={`bg-gray-50 border-gray-200 cursor-pointer transition-all hover:shadow-md ${filterStatus === 'closed' ? 'ring-2 ring-gray-400' : ''}`}
          onClick={() => setFilterStatus(filterStatus === 'closed' ? 'all' : 'closed')}
          data-testid="stat-closed-leads"
        >
          <CardContent className="p-4 flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-gray-100 flex items-center justify-center">
              <XCircle className="w-5 h-5 text-gray-600" />
            </div>
            <div>
              <p className="text-2xl font-bold text-gray-700">{leads.filter(l => l.status === "closed").length}</p>
              <p className="text-xs text-gray-600">{t('physician.closed_status')}</p>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Leads List */}
      {filteredLeads.length === 0 ? (
        <Card className="bg-white rounded-3xl border border-orange-100/50 p-8 text-center">
          <div className="flex flex-col items-center gap-4">
            <div className="w-16 h-16 bg-orange-100 rounded-full flex items-center justify-center">
              <Users className="w-8 h-8 text-primary" />
            </div>
            <h3 className="font-heading text-xl font-semibold text-secondary">
              {filterStatus === "all" ? t('physician.no_leads_yet') : t('physician.no_leads_filtered').replace('{status}', filterStatus)}
            </h3>
            <p className="text-muted-foreground max-w-md">
              {filterStatus === "all" 
                ? t('physician.leads_empty_desc')
                : t('physician.no_leads_filtered').replace('{status}', filterStatus)}
            </p>
          </div>
        </Card>
      ) : (
        <div className="space-y-3">
          {filteredLeads.map((lead) => (
            <Card
              key={lead.lead_id}
              className="bg-white rounded-2xl border border-orange-100/50 hover:shadow-md transition-all"
              data-testid={`lead-card-${lead.lead_id}`}
            >
              <CardContent className="p-4">
                <div className="flex items-start justify-between gap-4">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <h3 className="font-semibold text-secondary">{lead.patient_name}</h3>
                      {getStatusBadge(lead.status)}
                      {lead.diabetes_type && (
                        <Badge variant="outline" className="text-xs">
                          {getDiabetesTypeLabel(lead.diabetes_type)}
                        </Badge>
                      )}
                    </div>
                    
                    <div className="mt-2 flex flex-wrap gap-x-4 gap-y-1 text-sm text-muted-foreground">
                      <span className="flex items-center gap-1">
                        <Mail className="w-3.5 h-3.5" />
                        {lead.patient_email}
                      </span>
                      {lead.patient_phone && (
                        <span className="flex items-center gap-1">
                          <Phone className="w-3.5 h-3.5" />
                          {lead.patient_phone}
                        </span>
                      )}
                      <span className="flex items-center gap-1">
                        <Clock className="w-3.5 h-3.5" />
                        {formatDate(lead.created_at)}
                      </span>
                    </div>
                    
                    {lead.message && (
                      <p className="mt-2 text-sm text-muted-foreground bg-gray-50 p-2 rounded-lg">
                        "{lead.message}"
                      </p>
                    )}
                    
                    {lead.notes && (
                      <p className="mt-2 text-sm text-muted-foreground italic">
                        Notes: {lead.notes}
                      </p>
                    )}
                  </div>
                  
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => handleUpdateStatus(lead)}
                    className="rounded-full flex-shrink-0"
                    data-testid={`update-lead-${lead.lead_id}`}
                  >
                    Update Status
                    <ChevronRight className="w-4 h-4 ml-1" />
                  </Button>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {/* Update Status Modal */}
      <Dialog open={showUpdateModal} onOpenChange={setShowUpdateModal}>
        <DialogContent className="rounded-3xl max-w-md">
          <DialogHeader>
            <DialogTitle className="font-heading text-xl">Update Lead Status</DialogTitle>
            <DialogDescription>
              {selectedLead && `Update status for ${selectedLead.patient_name}`}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <label className="text-sm font-medium text-secondary">Status</label>
              <Select value={updateForm.status} onValueChange={(v) => setUpdateForm({ ...updateForm, status: v })}>
                <SelectTrigger className="rounded-xl" data-testid="update-lead-status-select">
                  <SelectValue placeholder="Select status" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="new">{t('physician.new_status')}</SelectItem>
                  <SelectItem value="contacted">{t('physician.contacted_status')}</SelectItem>
                  <SelectItem value="converted">{t('physician.converted_patient')}</SelectItem>
                  <SelectItem value="closed">{t('physician.closed_not_interested')}</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <label className="text-sm font-medium text-secondary">Notes (Optional)</label>
              <Textarea
                value={updateForm.notes}
                onChange={(e) => setUpdateForm({ ...updateForm, notes: e.target.value })}
                placeholder="Add notes about this lead..."
                className="rounded-xl min-h-[100px]"
                data-testid="update-lead-notes"
              />
            </div>
          </div>
          <DialogFooter className="gap-2">
            <Button
              variant="outline"
              onClick={() => setShowUpdateModal(false)}
              className="rounded-full"
            >
              Cancel
            </Button>
            <Button
              onClick={submitStatusUpdate}
              disabled={updating}
              className="rounded-full bg-primary text-white hover:bg-primary/90"
              data-testid="submit-lead-update"
            >
              {updating ? "Updating..." : "Update Status"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default DietitianLeads;
